import _ from "lodash";
import { Cache } from "./cache";
const minimumLocalResults = 5;
export const asyncDebounce = (func, wait, options) => {
    const debounce = _.debounce((resolve, reject, args) => {
        func(...args)
            .then(resolve)
            .catch(reject);
    }, wait, options);
    return (...args) => new Promise((resolve, reject) => {
        debounce(resolve, reject, args);
    });
};
export class CachedSearch {
    constructor({ search, localResultMatches, identity, getCacheKey, include, minLength = 3, }) {
        this.cache = new Cache();
        this.callSearch = asyncDebounce((partialName) => this.search(partialName), 300);
        this.search = search;
        this.localResultMatches = localResultMatches;
        this.identity = identity;
        this.getCacheKey = getCacheKey;
        this.minLength = minLength;
        include &&
            this.getCacheKey().then((k) => this.cache.addOrGetExisting(k, () => [
                { partialName: "", results: include },
            ]));
    }
    async get(partialName) {
        const previousQueries = await this.cache.addOrGetExisting(await this.getCacheKey(), () => []);
        const identicalPreviousQuery = previousQueries.find((q) => q.partialName === partialName);
        if (identicalPreviousQuery) {
            return {
                local: identicalPreviousQuery.results.filter((r) => this.localResultMatches(partialName, r)),
            };
        }
        if (this.previousQueryReturnedNoResults(previousQueries, partialName)) {
            return {
                local: [],
            };
        }
        const previousMatchingResults = _.uniqBy(previousQueries
            .flatMap((q) => q.results)
            .filter((r) => this.localResultMatches(partialName, r)), (r) => this.identity(r));
        if (previousMatchingResults.length >= minimumLocalResults ||
            partialName.length < this.minLength) {
            return {
                local: previousMatchingResults,
            };
        }
        const getRemoteResults = async () => {
            const searchResponse = await this.callSearch(partialName);
            if (!searchResponse.success)
                return [];
            const results = _.uniqBy(searchResponse.results, (r) => this.identity(r));
            previousQueries.push({
                partialName,
                results,
            });
            return results;
        };
        return {
            local: previousMatchingResults,
            remote: getRemoteResults(),
        };
    }
    previousQueryReturnedNoResults(previousQueries, partialName) {
        return _.some(previousQueries, (q) => !_.some(q.results) && partialName.startsWith(q.partialName));
    }
}
